import i18n from 'modules/core/lib/i18n'

import { useSchedule } from 'modules/shift_management/hooks/useSchedule'

import { formatDuration } from './EmployeeInfoCell/HoursDisplay'

const formatHours = (hours: number) => {
  const milliseconds = hours * 60 * 60 * 1000
  return formatDuration(milliseconds)
}

type TooltipState =
  | 'over_maximum_weekly'
  | 'over_maximum_weekly_no_leaves_or_bank_holidays'
  | 'over_maximum_weekly_no_leaves'
  | 'over_maximum_weekly_no_bank_holidays'
  | 'over_maximum_multiple_periods'
  | 'over_maximum_single_period'
  | 'no_hours'
  | 'all_hours'
  | 'some_hours'

interface PlannedHoursTooltipParams {
  isOverMaximum: boolean
  noHoursPlanned: boolean
  allHoursPlanned: boolean
  totalDuration: number
  overPlannedPeriods: Array<{ week: number }>
  maximumWeeklyHours: number
  leaveHours: number
  bankHolidayHours: number
}

const getTooltipState = (
  params: PlannedHoursTooltipParams,
  view: { isWeekly: boolean }
): TooltipState => {
  if (params.isOverMaximum) {
    if (view.isWeekly) {
      if (params.leaveHours === 0 && params.bankHolidayHours === 0)
        return 'over_maximum_weekly_no_leaves_or_bank_holidays'
      if (params.leaveHours === 0) return 'over_maximum_weekly_no_leaves'
      if (params.bankHolidayHours === 0) return 'over_maximum_weekly_no_bank_holidays'
      return 'over_maximum_weekly'
    } else {
      return params.overPlannedPeriods.length > 1
        ? 'over_maximum_multiple_periods'
        : 'over_maximum_single_period'
    }
  }

  if (params.noHoursPlanned) return 'no_hours'
  if (params.allHoursPlanned) return 'all_hours'
  return 'some_hours'
}

const tooltipMessages = {
  over_maximum_weekly_no_leaves_or_bank_holidays: (params: PlannedHoursTooltipParams) =>
    i18n.t(
      'shift_management.hours_information.bar_tooltips.over_hours_week_no_leaves_or_bank_holidays',
      {
        planned_hours: formatDuration(params.totalDuration),
        maximum_weekly_hours: formatHours(params.maximumWeeklyHours),
      }
    ),

  over_maximum_weekly_no_leaves: (params: PlannedHoursTooltipParams) =>
    i18n.t('shift_management.hours_information.bar_tooltips.over_hours_week_no_leaves', {
      planned_hours: formatDuration(params.totalDuration),
      public_holiday_hours: formatHours(params.bankHolidayHours),
      maximum_weekly_hours: formatHours(params.maximumWeeklyHours),
    }),

  over_maximum_weekly_no_bank_holidays: (params: PlannedHoursTooltipParams) =>
    i18n.t('shift_management.hours_information.bar_tooltips.over_hours_week_no_bank_holidays', {
      planned_hours: formatDuration(params.totalDuration),
      approved_leave_hours: formatHours(params.leaveHours),
      maximum_weekly_hours: formatHours(params.maximumWeeklyHours),
    }),

  over_maximum_weekly: (params: PlannedHoursTooltipParams) =>
    i18n.t('shift_management.hours_information.bar_tooltips.over_hours_week', {
      planned_hours: formatDuration(params.totalDuration),
      approved_leave_hours: formatHours(params.leaveHours),
      public_holiday_hours: formatHours(params.bankHolidayHours),
      maximum_weekly_hours: formatHours(params.maximumWeeklyHours),
    }),

  over_maximum_multiple_periods: (params: PlannedHoursTooltipParams) =>
    i18n.t('shift_management.hours_information.bar_tooltips.multiple_over_hours_period', {
      weeks: params.overPlannedPeriods
        .slice(0, -1)
        .map((period) => period.week.toString())
        .join(', '),
      last_week: params.overPlannedPeriods[params.overPlannedPeriods.length - 1]?.week.toString(),
    }),

  over_maximum_single_period: (params: PlannedHoursTooltipParams) =>
    i18n.t('shift_management.hours_information.bar_tooltips.over_hours_period', {
      week: params.overPlannedPeriods[0]?.week.toString(),
    }),

  no_hours: () => i18n.t('shift_management.hours_information.bar_tooltips.no_hours'),

  all_hours: () => i18n.t('shift_management.hours_information.bar_tooltips.all_hours'),

  some_hours: () => i18n.t('shift_management.hours_information.bar_tooltips.some_hours'),
}

const usePlannedHoursTooltip = (params: PlannedHoursTooltipParams) => {
  const [{ view }] = useSchedule()

  const tooltipState = getTooltipState(params, view)
  const plannedHoursTooltipCopy = tooltipMessages[tooltipState](params)

  return {
    plannedHoursTooltipCopy,
    tooltipState,
  }
}

export default usePlannedHoursTooltip
